import React, { useContext, useCallback } from 'react';
import { useActor } from '@xstate/react';
import { GlobalStateContext } from '../../state/app/index';
import { useHotkeys } from 'react-hotkeys-hook';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CloseIcon from '@mui/icons-material/Close';

// ── helpers ───────────────────────────────────────────────────────────────────

const DANGER_ACTIONS = ['delete', 'trash'];

function getModalMeta(title = '', primaryTitle = '') {
  const t = title.toLowerCase();
  const isDanger = DANGER_ACTIONS.includes((primaryTitle || '').toLowerCase());

  let icon;
  if (t.includes('rename') || t.includes('update')) {
    icon = <EditIcon sx={{ fontSize: '2rem', color: '#fff' }} />;
  } else if (t.includes('delete') || t.includes('trash') || t.includes('sure')) {
    icon = <DeleteForeverIcon sx={{ fontSize: '2rem', color: '#fff' }} />;
  } else if (t.includes('create') || t.includes('new')) {
    icon = <AddCircleOutlineIcon sx={{ fontSize: '2rem', color: '#fff' }} />;
  } else if (t.includes('merge')) {
    icon = <CallMergeIcon sx={{ fontSize: '2rem', color: '#fff' }} />;
  } else {
    icon = <FolderOutlinedIcon sx={{ fontSize: '2rem', color: '#fff' }} />;
  }

  return { icon, isDanger };
}

// ── component ─────────────────────────────────────────────────────────────────

const Modal = () => {
  const globalServices = useContext(GlobalStateContext);
  const [modalState, sendModalState] = useActor(globalServices.modalState);

  const {
    context: { content, primaryButton, sectionClassName, showCloseModal, title, secondaryButton },
  } = modalState;

  const handleCloseModal = useCallback(() => {
    sendModalState({ type: 'closeModal' });
  }, [sendModalState]);

  const isModalActive = modalState.matches('active');
  useHotkeys('esc', handleCloseModal, { enabled: isModalActive, enableOnFormTags: true }, [handleCloseModal]);

  if (!modalState.matches('active')) return null;

  // ── user-logs-modal: full-screen, keep legacy layout ─────────────────────
  if (sectionClassName?.includes('user-logs-modal')) {
    return (
      <div
        className="modal-backdrop"
        onMouseDown={() => sendModalState({ type: 'closeModal' })}
      >
        <div
          className={`dry-modal ${sectionClassName}`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                {title && (
                  <h1 className="text-lg leading-6 font-medium" id="modal-title"
                    style={{ color: '#ffffff', marginBottom: '0.5rem' }}>
                    {title}
                  </h1>
                )}
                <div className="mt-2">
                  <section className="text-sm text-gray-500">{content}</section>
                </div>
              </div>
            </div>
          </div>
          <div id="modal-actions" className="">
            {!!primaryButton?.show && typeof primaryButton.callback === 'function' && (
              <button className="bg-primary text-base font-semibold text-white primary-button"
                onClick={primaryButton.callback} type="button">
                {primaryButton.title || 'Confirm'}
              </button>
            )}
            {!!secondaryButton?.show && typeof secondaryButton.callback === 'function' && (
              <button className="secondary-button bg-slate-500 text-base font-semibold text-white"
                onClick={secondaryButton.callback} type="button">
                {secondaryButton.title || 'Cancel'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── split-panel modal ─────────────────────────────────────────────────────
  const { icon, isDanger } = getModalMeta(title, primaryButton?.title || '');

  return (
    <div className="modal-backdrop" onMouseDown={handleCloseModal}>
      <div
        className={`dry-modal--split${sectionClassName ? ` ${sectionClassName}` : ''}`}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Left accent strip */}
        <div className={`dry-modal__accent${isDanger ? ' dry-modal__accent--danger' : ''}`}>
          <div className="dry-modal__accent-icon">{icon}</div>
        </div>

        {/* Right content */}
        <div className="dry-modal__body">
          <div className="dry-modal__header">
            <h2 className="dry-modal__title">{title}</h2>
            {showCloseModal && (
              <button
                className="dry-modal__close"
                onClick={handleCloseModal}
                type="button"
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: '1.1rem' }} />
              </button>
            )}
          </div>

          <div className="dry-modal__content">
            {content || null}
          </div>

          <div className="dry-modal__footer">
            {!!secondaryButton?.show && typeof secondaryButton.callback === 'function' && (
              <button
                className="dry-modal__btn dry-modal__btn--secondary"
                onClick={secondaryButton.callback}
                type="button"
              >
                {secondaryButton.title || 'Cancel'}
              </button>
            )}
            {!!primaryButton?.show && typeof primaryButton.callback === 'function' && (
              <button
                className={`dry-modal__btn dry-modal__btn--primary${isDanger ? ' dry-modal__btn--danger' : ''}`}
                onClick={primaryButton.callback}
                type="button"
              >
                {primaryButton.title || 'Confirm'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
